'use client';

import { RefObject, useRef, useState } from 'react';
import { Box } from '@wishket/design-system';
import { useClickOutside } from '@wishket/yogokit';
import { usePathname, useRouter } from 'next/navigation';

import { useArticleLike } from './ArticleDetailMain.parts.hooks';
import { getShareParams, openNavigatorShare } from './ArticleDetailMain.utils';

import {
  useArticleActionStatus,
  useGetIsScrappedByArticleId,
} from '@/entities/article';
import { ScrapBox, ShareBox, Skeleton } from '@/shared/Components';
import { useAuth, useDeviceCheck } from '@/shared/hooks';
import { ARTICLE_TOP_BANNER_HEIGHT, PATH } from '@/shared/constants';
import { ArticleInteractiveToolbar } from '@/features/ArticleDetail';

interface ArticleToolbarProps {
  commentListRef: RefObject<HTMLDivElement | null>;
  id: string;
  articleTitle: string;
}

const ARTICLE_GAP = 32;

const ArticleDetailSideToolbar = ({
  commentListRef,
  id,
  articleTitle,
}: ArticleToolbarProps) => {
  const articleId = Number(id);
  const { isDesktop, isMobile } = useDeviceCheck();
  const [isScrapBoxOpen, setIsScrapBoxOpen] = useState(false);
  const [isShareBoxOpen, setIsShareBoxOpen] = useState(false);
  const { like, isLiked, comment } = useArticleActionStatus(articleId);
  const isScrapped = useGetIsScrappedByArticleId(articleId);
  const { handleLikeAction } = useArticleLike(id);
  const pathname = usePathname();
  const { isAuth } = useAuth();
  const router = useRouter();

  const scrapBoxRef = useRef<HTMLDivElement>(null);
  const shareBoxRef = useRef<HTMLDivElement>(null);

  const { shareTitle, shareUrl } = getShareParams(articleTitle, id);

  const handleScrollToComments = () => {
    const currentTopBannerHeight = isMobile
      ? ARTICLE_TOP_BANNER_HEIGHT.MOBILE
      : ARTICLE_TOP_BANNER_HEIGHT.TABLET;

    if (commentListRef && commentListRef.current) {
      const scrollPosition =
        commentListRef.current.offsetTop - currentTopBannerHeight - ARTICLE_GAP;
      window.scrollTo({
        top: scrollPosition,
      });
    }
  };

  const openShareBox = () => {
    if (isScrapBoxOpen) {
      closeScrapBox();
    }
    setIsShareBoxOpen(true);
  };

  const closeShareBox = () => {
    setIsShareBoxOpen(false);
  };

  const openScrapBox = () => {
    if (!isAuth) {
      router.push(`${PATH.AUTH_LOGIN}?next=${pathname}`);
      return;
    }
    if (isShareBoxOpen) {
      closeShareBox();
    }
    setIsScrapBoxOpen(true);
  };

  const closeScrapBox = () => {
    setIsScrapBoxOpen(false);
  };

  useClickOutside(scrapBoxRef, closeScrapBox);
  useClickOutside(shareBoxRef, closeShareBox);

  if (!like || !comment) return <Skeleton className="h-[245px] w-[56px]" />;

  return (
    <>
      <ArticleInteractiveToolbar
        toolbarProps={{
          like,
          isLiked,
          comment,
          isScrapped,
          isScrapBoxOpen,
          isShareBoxOpen,
          scrapButtonId: 'scrap-button',
          onLikeClick: handleLikeAction,
          onCommentClick: handleScrollToComments,
          onScrapClick: openScrapBox,
          onShareClick: () => {
            isDesktop
              ? openShareBox()
              : openNavigatorShare(shareTitle, shareUrl);
          },
        }}
        commentListRef={commentListRef}
      >
        <Box className="relative">
          <ScrapBox
            className="absolute -top-[116px] left-[58px] z-30"
            scrapBoxRef={scrapBoxRef}
            articleId={articleId}
            isOpen={isScrapBoxOpen}
            onClose={closeScrapBox}
          />
          {isShareBoxOpen && (
            <Box className="absolute -top-[50px] left-[58px]" ref={shareBoxRef}>
              <ShareBox url={shareUrl} title={shareTitle} />
            </Box>
          )}
        </Box>
      </ArticleInteractiveToolbar>
    </>
  );
};

export default ArticleDetailSideToolbar;
